import type { JSX } from 'react';

import { Event, EventView, GUID } from '@ndvr/customer-api';

import { CalendarIcon } from '@/components/Icons';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import TaskOverlays from 'views/tasks/TaskOverlays';

import { EventDetailsDescriptionSection } from './EventDetailsDescriptionSection';
import { EventDetailsDrawerActions } from './EventDetailsDrawerActions';
import { EventDetailsEditNotice } from './EventDetailsEditNotice';
import { EventDetailsMeta } from './EventDetailsMeta';
import { EventDetailsNotesSection } from './EventDetailsNotesSection';
import { EventDetailsTasksSection } from './EventDetailsTasksSection';
import { useGetEvent } from './events-queries';
import { type EventDetailsActionProps, useEventDetailsModel } from './useEventDetailsModel';

export interface EventDetailsDrawerProps {
    event: EventView | null;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

export function EventDetailsDrawer({ event, isOpen, onOpenChange }: EventDetailsDrawerProps): JSX.Element | null {
    if (!event) {
        return null;
    }

    return <EventDetailsDrawerContent event={event} isOpen={isOpen} onOpenChange={onOpenChange} />;
}

export interface EventDetailsDrawerByGuidProps {
    eventGUID: GUID<Event>;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

// For callers that only hold the GUID (like TaskViewDetailsSheet's taskGuid): a placeholder sheet,
// sized like the drawer, covers loading, failed and missing events until the event arrives.
export function EventDetailsDrawerByGuid({
    eventGUID,
    isOpen,
    onOpenChange,
}: EventDetailsDrawerByGuidProps): JSX.Element {
    const eventQuery = useGetEvent(eventGUID);

    if (eventQuery.data) {
        return <EventDetailsDrawerContent event={eventQuery.data} isOpen={isOpen} onOpenChange={onOpenChange} />;
    }

    const title = eventQuery.isLoading
        ? 'Loading event details...'
        : eventQuery.isError
          ? 'Unable to load event'
          : 'Event not found';

    return (
        <Sheet open={isOpen} onOpenChange={onOpenChange}>
            <SheetContent className="flex w-[400px] flex-col sm:w-[540px]" aria-describedby={undefined}>
                <SheetHeader className="flex-shrink-0">
                    <SheetTitle>{title}</SheetTitle>
                </SheetHeader>
            </SheetContent>
        </Sheet>
    );
}

interface EventDetailsDrawerContentProps {
    event: EventView;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

const EventDetailsDrawerContent = ({
    event: initialEvent,
    isOpen,
    onOpenChange,
}: EventDetailsDrawerContentProps): JSX.Element => {
    const { data: freshEvent } = useGetEvent(initialEvent.eventGUID as unknown as GUID<Event>);
    const event = freshEvent ?? initialEvent;

    const {
        canInviteMeetingBot,
        canInviteMeetingBotDisabledReason,
        canScheduleMeetingBot,
        canJoinMeeting,
        canStopMeetingBot,
        canUnscheduleMeetingBot,
        eventDate,
        eventTimeRange,
        meetingBot,
        notes,
        onCreateNote,
        onCreateTask,
        onInviteMeetingBot,
        onScheduleMeetingBot,
        onNoteClick,
        onRemoveMeetingBot,
        onTaskClick,
        tasks,
    } = useEventDetailsModel({
        event,
        meetingBotEnabled: isOpen,
        onRequestClose: () => onOpenChange(false),
    });
    const actionProps: EventDetailsActionProps = {
        canInviteMeetingBot,
        canInviteMeetingBotDisabledReason,
        canScheduleMeetingBot,
        canJoinMeeting,
        canStopMeetingBot,
        canUnscheduleMeetingBot,
        eventUrl: event.url,
        onCreateNote,
        onCreateTask,
        onInviteMeetingBot,
        onScheduleMeetingBot,
        onRemoveMeetingBot,
    };

    return (
        <>
            <Sheet open={isOpen} onOpenChange={onOpenChange}>
                <SheetContent className="flex w-[400px] flex-col sm:w-[540px]">
                    <SheetHeader className="flex-shrink-0">
                        <SheetTitle className="text-ndvr-dark flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5" />
                            Event Details
                        </SheetTitle>
                    </SheetHeader>

                    <div className="mt-6 flex-1 space-y-6 overflow-y-auto pb-6">
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-semibold">{event.title}</h3>
                            </div>
                            <EventDetailsMeta
                                event={event}
                                eventDate={eventDate}
                                eventTimeRange={eventTimeRange}
                                meetingBot={meetingBot}
                            />
                        </div>

                        <EventDetailsNotesSection notes={notes} onNoteClick={onNoteClick} />
                        <EventDetailsTasksSection tasks={tasks} onTaskClick={onTaskClick} />
                        <EventDetailsDescriptionSection description={event.description} eventGuid={event.eventGUID} />
                        <EventDetailsEditNotice />
                        <EventDetailsDrawerActions {...actionProps} />
                    </div>
                </SheetContent>
            </Sheet>

            <TaskOverlays />
        </>
    );
};
